import React, { useEffect, useState, useContext } from "react";
import { Box, Tab, Typography } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { removeEntity, setEntity } from "../../common/CommonFunctions";
import favoritesManagementService from "../../services/FavoritesManagementService";
import CardGrid from "../../components/CardGrid/CardGrid";
import ContentCard from "../../components/ContentCard/ContentCard";
import MUILoader from "../../components/MUILoader/MUILoader";
import dayjs from "dayjs";
import { AppGlobalContext } from "../../contexts/AppContext.context";
import { AppTheme } from "../../theme/App.theme";
import ContentManagement from "../ContentManagement/ContentManagement";
import { CONTENT_TYPES_CONFIG } from "../../config/App.config";
import { Favorite } from "@mui/icons-material";

const FavoriteSection = () => {
  const { authUserDetails } = useContext(AppGlobalContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [favoritesData, setFavoritesData] = useState({});
  const [loading, setLoading] = useState(true);

  // TabList SX (MUI - Style)
  const TabList_SX = {
    minHeight: 40,
    "& .MuiTabs-indicator": {
      backgroundColor: AppTheme.PRIMARY_COLOR,
      height: "3px",
    },
    "& .MuiTabs-scrollButtons": {
      opacity: 1, // Ensure scroll buttons are always visible
      visibility: "visible", // Ensure scroll buttons are visible
      "&.Mui-disabled": {
        opacity: 0.4, // Change opacity when buttons are disabled (optional)
      },
    },
  };

  // Tab SX (MUI - Style)
  const Tab_SX = {
    minHeight: 40,
    fontSize: "14px",
    borderBottom: `1px solid ${AppTheme.BORDER_COLOR}`,
    "&.Mui-selected": {
      color: AppTheme.PRIMARY_COLOR,
      borderBottomColor: AppTheme.PRIMARY_COLOR,
    },
  };

  // Tab configuration
  const tabs = [
    { value: "all", label: "All" },
    { value: "podcast", label: "Podcasts" },
    { value: "bhaktisangeet", label: "Bhakti Sangeet" },
    { value: "topic", label: "Educational Topics" },
    { value: "chapter", label: "Chapters" },
    { value: "news", label: "Devotional News" },
    { value: "information", label: "Information Section" },
    { value: "ebook", label: "E-Books" },
    // { value: "magazine", label: "Magazines" },
  ];

  // Fetch favorites data
  const fetchFavorites = async () => {
    setFavoritesData({});
    setLoading(true);
    try {
      const response = await favoritesManagementService.getAll({
        language: authUserDetails?.preferredLanguage || "English",
      });
      const { data, statusCode } = response;

      if (statusCode === 200) {
        setFavoritesData(data || {});
      }
    } catch (error) {
      console.error("Error while fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Check if all favorites are empty
  const isAllFavoritesEmpty = () => {
    if (!favoritesData || Object.keys(favoritesData).length === 0) {
      return true;
    }
    
    // Check if all content types have empty arrays
    const contentTypes = ['podcast', 'bhaktisangeet', 'topic', 'chapter', 'news', 'information', 'ebook'];
    return contentTypes.every(type => {
      const items = favoritesData[type] || [];
      return !Array.isArray(items) || items.length === 0;
    });
  };

  // Debounced search effect
  useEffect(() => {
    if (!authUserDetails?.uid) return;
    if (activeTab === "all") {
      fetchFavorites();
    }
    removeEntity();
  }, [authUserDetails, activeTab]);

  // Handle entity click for navigation
  const handleEntityClick = (item, contentType) => {
    setEntity(item);
    const routes = {
      podcast: "/podcast",
      ebook: "/e-books",
      magazine: "/magazine",
      topic: "/educational-topics",
      information: "/information-section",
      news: "/devotional-news",
      bhaktisangeet: "/bhakti-sangeet",
      chapter: "/educational-chapters",
    };
    const route = routes[contentType];
    if (route) {
      navigate(route);
    }
  };

  // Render function for ContentCard
  const renderContentCard = (card, index, onItemClick, layout) => {
    let dateStr = "";

    if (card?.updatedAt) {
      if (typeof card.updatedAt === "string") {
        // Already a string, just format
        dateStr = dayjs(card.updatedAt).format("D MMM, YYYY");
      } else if (
        typeof card.updatedAt === "object" &&
        card.updatedAt._seconds
      ) {
        // Firestore timestamp object
        dateStr = dayjs(card.updatedAt._seconds * 1000).format("D MMM, YYYY");
      }
    }
    return (
      <ContentCard
        layout={layout || "vertical"}
        image={card.thumbnailImage || card.thumbnail || card.image}
        title={card?.topicName || card?.title || card?.name}
        subtitle={dateStr}
        onClick={() => onItemClick(card, index)}
      />
    );
  };

  // Empty state component
  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center',
        // padding: '2rem',
      }}
    >
      <Box
        sx={{
          fontSize: '2.5rem',
        }}
      >
        <Favorite sx={{ color: "#FF1717" }} fontSize="inherit" />
      </Box>
      <Typography
        className="plus-jakarta-sans-semi-bold-600"
        variant="h6"
        sx={{
          marginBottom: '0.5rem',
          color: 'text.primary',
          fontWeight: 600,
        }}
      >
        No Favorites Yet
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          maxWidth: '400px',
        }}
      >
        Start exploring our content and add items to your favorites by clicking the heart icon. 
        Your saved content will appear here for easy access.
      </Typography>
    </Box>
  );

  // Dashboard-like section for "All" tab
  const renderAllTab = () => {
    // Show empty state if all favorites are empty
    if (isAllFavoritesEmpty() && !loading) {
      return renderEmptyState()
    }

    const sections = [
      {
        key: "podcast",
        title: "Podcasts",
        layout: "vertical",
        onSeeAll: () => setActiveTab("podcast"),
        enableHorizontalScroll: true,
        showSeeAll: true,
      },
      {
        key: "bhaktisangeet",
        title: "Bhakti Sangeet",
        layout: "vertical",
        onSeeAll: () => setActiveTab("bhaktisangeet"),
        enableHorizontalScroll: true,
        showSeeAll: true,
      },
      {
        key: "topic",
        title: "Educational Topics",
        layout: "vertical",
        onSeeAll: () => setActiveTab("topic"),
        enableHorizontalScroll: true,
        showSeeAll: true,
      },
      {
        key: "chapter",
        title: "Chapters",
        layout: "vertical",
        onSeeAll: () => setActiveTab("chapter"),
        enableHorizontalScroll: true,
        showSeeAll: true,
      },
      {
        key: "news",
        title: "Devotional News",
        layout: "horizontal",
        size: { xs: 12, sm: 6, md: 6, lg: 6 },
        onSeeAll: () => setActiveTab("news"),
        showSeeAll: true,
      },
      {
        key: "information",
        title: "Information Section",
        layout: "horizontal",
        size: { xs: 12, sm: 6, md: 6, lg: 6 },
        onSeeAll: () => setActiveTab("information"),
        showSeeAll: true,
      },
      {
        key: "ebook",
        title: "E-Books",
        layout: "ebook",
        enableHorizontalScroll: true,
        onSeeAll: () => setActiveTab("ebook"),
        showSeeAll: true,
      },
      //   { key: 'magazine', title: 'Magazines', layout: 'vertical', enableHorizontalScroll: true },
    ];

    return (
      <Box>
        {sections.map((section) => {
          const items = favoritesData[section.key] || [];
          if (!items.length) return null;

          // Extract only the content objects
          const contents = items.map((item) => ({
            ...item.content,
            isFavorite: true,
          }));

          return (
            <div key={section.key} className="mb-4">
              <CardGrid
                data={contents}
                renderItem={(card, index, onItemClick) =>
                  renderContentCard(card, index, onItemClick, section.layout)
                }
                type={section.layout}
                onItemClick={(item, index) =>
                  handleEntityClick(item, section.key)
                }
                enableHorizontalScroll={section.enableHorizontalScroll || false}
                showHeader={true}
                headerTitle={section.title}
                showSeeAll={section.showSeeAll}
                onSeeAllClick={() => section.onSeeAll && section.onSeeAll()}
                size={section.size}
              />
            </div>
          );
        })}
      </Box>
    );
  };

  const renderContentTypeTab = (tab) => {
    const sections = [
      {
        key: "topic",
        contentType: CONTENT_TYPES_CONFIG.TOPIC,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "chapter",
        contentType: CONTENT_TYPES_CONFIG.CHAPTER,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "podcast",
        contentType: CONTENT_TYPES_CONFIG.PODCAST,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "bhaktisangeet",
        contentType: CONTENT_TYPES_CONFIG.AUDIO,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "information",
        contentType: CONTENT_TYPES_CONFIG.INFORMATION,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "news",
        contentType: CONTENT_TYPES_CONFIG.NEWS,
        layout: "horizontal",
        slug: "title",
      },
      {
        key: "ebook",
        layout: "ebook",
        contentType: CONTENT_TYPES_CONFIG.E_BOOK,
        slug: "title",
      },
    ];
  
    const section = sections.find((s) => s.key === tab);
  
    if (!section) return null;
  
    return (
      <ContentManagement
        service={favoritesManagementService}
        pageSize={10}
        sidebarItemLayout={section.layout}
        contentType={section.contentType}
        getSlug={section.slug}
        isFavoritesMode={true}
      />
    );
  };
  

  return (
    <React.Fragment>
      <div style={{ position: "relative" }}>
        {/* Tabs */}
        <TabContext value={activeTab}>
          <div
            style={{
              width: "100%",
            }}
          >
            <TabList
              onChange={handleTabChange}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ ...TabList_SX, marginBottom: 0 }}
            >
              {tabs.map((tab) => (
                <Tab
                  sx={{ ...Tab_SX }}
                  key={tab.value}
                  value={tab.value}
                  className="text-to-capitalize"
                  label={tab.label}
                />
              ))}
            </TabList>
          </div>
          {loading && <MUILoader />}
          {tabs.map((tab) => (
            <TabPanel key={tab.value} value={tab.value} className="px-0 pb-0 pt-2">
              {activeTab === "all"
                ? renderAllTab()
                : renderContentTypeTab(tab.value)}
            </TabPanel>
          ))}
        </TabContext>
      </div>
    </React.Fragment>
  );
};

export default FavoriteSection;
